import { useCallback } from "react";
import {
  getSelectedPropertyExpediaHotelIds,
  getHotelIdForPropertyId,
} from "../pages/price-check/utils";

export const usePriceCheckHandlers = ({
  formValues,
  handleSearch,
  priceCheckPage,
  setPriceCheckPage,
  sortState,
  setSortState,
  setSelectedFilters,
  setFilterSelectionOrder,
  activeTab,
  properties,
  setExpandedRowKeys,
}) => {
  // Helper function to get the appropriate hotel IDs based on current tab
  const getCurrentTabHotelIds = useCallback(() => {
    if (activeTab === "all" || !activeTab) {
      // For "all" tab, use all selected hotel IDs
      return getSelectedPropertyExpediaHotelIds(
        properties,
        formValues?.selectedCity || [],
        formValues?.selectedProperties || []
      );
    } else {
      // For specific property tab, use only that hotel ID
      // activeTab is now a propertyId, not a propertyName
      const hotelId = getHotelIdForPropertyId(properties, activeTab);
      return hotelId ? [hotelId] : [];
    }
  }, [
    activeTab,
    properties,
    formValues?.selectedCity,
    formValues?.selectedProperties,
  ]);

  const onPageChange = useCallback(
    (page, pageSize) => {
      // Close all expanded rows when changing page
      setExpandedRowKeys([]);

      setPriceCheckPage((prevState) => ({
        ...prevState,
        number: page - 1, // Store as 0-based for internal state
        size: pageSize,
      }));

      // Trigger new search with updated pagination - use UI page number (1-based)
      if (formValues && Object.keys(formValues).length > 0) {
        const currentTabHotelIds = getCurrentTabHotelIds();
        handleSearch(
          formValues,
          {
            page: page, // Pass 1-based page number to API
            pageSize: pageSize,
          },
          null, // overrideFilters
          null, // passedFilterSelectionOrder
          false, // isAutoSearch
          currentTabHotelIds // specificHotelIds for current tab
        );
      }
    },
    [
      formValues,
      handleSearch,
      setPriceCheckPage,
      getCurrentTabHotelIds,
      setExpandedRowKeys,
    ]
  );

  const handleSort = useCallback(
    (dataKey) => {
      let newSortDirection = "desc"; // Start with desc first

      // If already sorting by this column, toggle direction
      if (sortState.sortBy === dataKey) {
        newSortDirection = sortState.sortDirection === "desc" ? "asc" : "desc";
      }

      const newSortState = {
        sortBy: dataKey,
        sortDirection: newSortDirection,
      };

      setSortState(newSortState);

      // Trigger new search with sorting
      if (formValues && Object.keys(formValues).length > 0) {
        const currentTabHotelIds = getCurrentTabHotelIds();
        handleSearch(
          formValues,
          {
            page: priceCheckPage.number + 1, // Keep current page (convert from 0-based to 1-based)
            pageSize: priceCheckPage.size,
            sortBy: dataKey,
            sortDirection: newSortDirection,
          },
          null, // overrideFilters
          null, // passedFilterSelectionOrder
          false, // isAutoSearch
          currentTabHotelIds // specificHotelIds for current tab
        );
      }
    },
    [
      formValues,
      handleSearch,
      priceCheckPage.number,
      priceCheckPage.size,
      sortState,
      setSortState,
      getCurrentTabHotelIds,
    ]
  );

  const handleFiltersChange = useCallback(
    (
      apiFilters,
      apiFilterSelectionOrder = [],
      uiFilters = null,
      uiFilterSelectionOrder = null
    ) => {
      // Update state with UI filters (for display) or fallback to API filters
      const filtersToStore = uiFilters || apiFilters;
      const selectionOrderToStore =
        uiFilterSelectionOrder || apiFilterSelectionOrder;

      setSelectedFilters(filtersToStore);
      setFilterSelectionOrder(selectionOrderToStore);

      // Trigger new search with API filters if we have form values
      if (formValues && Object.keys(formValues).length > 0) {
        const currentTabHotelIds = getCurrentTabHotelIds();
        handleSearch(
          formValues,
          {
            page: 1, // Reset to first page when filtering
            pageSize: priceCheckPage.size,
          },
          apiFilters, // Use API filters for search
          apiFilterSelectionOrder,
          false, // isAutoSearch
          currentTabHotelIds // specificHotelIds for current tab
        );
      }
    },
    [
      formValues,
      handleSearch,
      priceCheckPage.size,
      setSelectedFilters,
      setFilterSelectionOrder,
      getCurrentTabHotelIds,
    ]
  );

  return {
    onPageChange,
    handleSort,
    handleFiltersChange,
  };
};
